import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { focusRingClassName } from '@digiwedge/ui-headless';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { getRecentItems, recordRecentItem } from './appLauncherRecents.js';
import { useAppLauncherKeyboardNav } from './useAppLauncherKeyboardNav.js';
import { AppLauncherEmptyState, AppLauncherHighlightCard, AppLauncherSearch, AppLauncherSections, AppLauncherTile, SectionGrid, SectionHeading, } from './appLauncherTiles.js';
const APP_LAUNCHER_COLUMNS = 3;
const RECENT_SECTION_LABEL = 'Recent';
/**
 * Focusable descendants of the launcher dialog. Roving-focus tiles expose
 * `tabindex="-1"` for the inactive cells, so the `:not([tabindex="-1"])` guard
 * keeps the trap aligned with the single active tile rather than every cell.
 */
const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';
const buildAppLauncherSearchValues = (item) => {
    const values = [item.label, item.description, item.section].filter((value) => Boolean(value));
    return values.flatMap((value) => {
        const normalised = value.toLowerCase();
        if (normalised.endsWith('ies')) {
            return [normalised, `${normalised.slice(0, -3)}y`];
        }
        return [normalised];
    });
};
const filterAppLauncherItems = (items, query) => {
    const normalisedQuery = query.trim().toLowerCase();
    if (!normalisedQuery) {
        return items;
    }
    return items.filter((item) => buildAppLauncherSearchValues(item).some((value) => value.includes(normalisedQuery)));
};
const groupAppLauncherItems = (items) => {
    const groups = [];
    items.forEach((item) => {
        const section = item.section ?? 'Apps';
        const group = groups.find((candidate) => candidate.section === section);
        if (group) {
            group.items.push(item);
            return;
        }
        groups.push({ section, items: [item] });
    });
    return groups;
};
const isActiveRoute = (currentPath, currentSearch, itemKey) => {
    const [path = itemKey, rawSearch] = itemKey.split('?');
    const itemSearch = rawSearch ? `?${rawSearch}` : '';
    if (itemSearch) {
        return currentPath === path && currentSearch === itemSearch;
    }
    if (path === '/') {
        return currentPath === '/';
    }
    return currentPath === path || currentPath.startsWith(`${path}/`);
};
const buildDesktopSections = (groups, recents, showRecents) => showRecents && recents.length > 0
    ? [{ section: RECENT_SECTION_LABEL, items: recents }, ...groups]
    : groups;
/** Flatten ordered desktop sections into a single tile list for roving focus. */
const flattenSections = (sections) => sections.flatMap((section) => section.items);
const ResultCountHint = ({ count }) => (_jsx("p", { id: "teetime-app-launcher-results", className: "mt-2 px-1 text-[11px] text-gray-500", "aria-live": "polite", children: count === 0 ? 'No results' : `${count} ${count === 1 ? 'result' : 'results'} · ↵ to open` }));
const DesktopLauncherGrid = ({ sections, currentPath, currentSearch, nav, onSelect }) => {
    let runningIndex = 0;
    return (_jsx("div", { className: "space-y-3", role: "group", "aria-label": "App launcher destinations", children: sections.map((section) => (_jsxs("section", { children: [_jsx(SectionHeading, { density: "desktop", children: section.section }), _jsx(SectionGrid, { density: "desktop", children: section.items.map((item) => {
                        const index = runningIndex;
                        runningIndex += 1;
                        return (_jsx(AppLauncherTile, { item: item, density: "desktop", isActive: isActiveRoute(currentPath, currentSearch, item.key), tabIndex: nav.getTabIndex(index), tileRef: nav.registerTile(index), onKeyDown: nav.handleTileKeyDown(index), onSelect: () => onSelect(item) }, `desktop-${section.section}-${item.key}`));
                    }) })] }, `desktop-${section.section}`))) }));
};
export const TeeTimeDesktopAppLauncher = ({ items, label, highlight, onBeforeOpen }) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const [recentsTick, setRecentsTick] = React.useState(0);
    const buttonRef = React.useRef(null);
    const panelRef = React.useRef(null);
    const searchRef = React.useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const filtered = React.useMemo(() => filterAppLauncherItems(items, query), [items, query]);
    const groups = React.useMemo(() => groupAppLauncherItems(filtered), [filtered]);
    const hasQuery = query.trim().length > 0;
    // recentsTick forces re-resolution after a selection updates localStorage.
    const recents = React.useMemo(() => (hasQuery ? [] : getRecentItems(items)), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, hasQuery, recentsTick]);
    const sections = React.useMemo(() => buildDesktopSections(groups, recents, !hasQuery), [groups, recents, hasQuery]);
    const orderedTiles = React.useMemo(() => flattenSections(sections), [sections]);
    const nav = useAppLauncherKeyboardNav(orderedTiles.length, APP_LAUNCHER_COLUMNS);
    const resultCount = filtered.length;
    const close = React.useCallback(() => {
        setOpen(false);
        setQuery('');
        buttonRef.current?.focus();
    }, []);
    // Honour the dialog contract: trap Tab/Shift+Tab within the panel so keyboard
    // focus cannot silently escape to the page behind the open launcher, and
    // close on Escape returning focus to the trigger.
    const handlePanelKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        const panel = panelRef.current;
        if (!panel) {
            return;
        }
        const focusable = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }, [close]);
    const select = React.useCallback((item) => {
        recordRecentItem(item.key);
        setRecentsTick((tick) => tick + 1);
        setOpen(false);
        setQuery('');
    }, []);
    const selectHighlight = React.useCallback(() => {
        setOpen(false);
        setQuery('');
    }, []);
    const handleSearchKeyDown = React.useCallback((event) => {
        if (event.key === 'Enter') {
            const top = orderedTiles[0];
            if (!top) {
                return;
            }
            event.preventDefault();
            recordRecentItem(top.key);
            setRecentsTick((tick) => tick + 1);
            setOpen(false);
            setQuery('');
            navigate(top.key);
            buttonRef.current?.focus();
            return;
        }
        if (event.key === 'ArrowDown' && orderedTiles.length > 0) {
            event.preventDefault();
            nav.focusFirstTile();
        }
    }, [navigate, nav, orderedTiles]);
    React.useEffect(() => {
        if (!open) {
            return undefined;
        }
        searchRef.current?.focus();
        const handlePointerDown = (event) => {
            const target = event.target;
            if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
            setQuery('');
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [open]);
    return (_jsxs("div", { className: "relative hidden md:block", children: [_jsx("button", { ref: buttonRef, type: "button", className: `flex items-center rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-primary ${focusRingClassName}`, "aria-label": label, "aria-expanded": open, "aria-controls": "teetime-app-launcher", title: label, onClick: () => {
                    setOpen((current) => {
                        if (current) {
                            setQuery('');
                            return false;
                        }
                        onBeforeOpen?.();
                        return true;
                    });
                }, children: _jsx(Squares2X2Icon, { className: "h-5 w-5" }) }), open && (_jsxs("div", { ref: panelRef, id: "teetime-app-launcher", role: "dialog", "aria-modal": "true", "aria-label": label, className: "absolute right-0 z-[130] mt-2 w-[420px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl", onKeyDown: handlePanelKeyDown, children: [_jsxs("div", { className: "flex items-center justify-between border-b border-gray-100 px-4 py-3", children: [_jsx("span", { className: "text-sm font-semibold text-gray-900", children: label }), _jsx(Link, { to: "/", className: "text-xs font-medium text-primary hover:underline", onClick: () => {
                                    setOpen(false);
                                    setQuery('');
                                }, children: "Home" })] }), _jsxs("div", { className: "border-b border-gray-100 p-3", children: [_jsx(AppLauncherSearch, { id: "teetime-app-launcher-search", value: query, placeholder: "Search actions, bookings, buddies", inputRef: searchRef, describedBy: hasQuery ? 'teetime-app-launcher-results' : undefined, onChange: setQuery, onKeyDown: handleSearchKeyDown }), hasQuery && _jsx(ResultCountHint, { count: resultCount })] }), _jsxs("div", { className: "max-h-[min(640px,calc(100vh-12rem))] overflow-y-auto p-3", children: [!hasQuery && highlight && (_jsx("div", { className: "mb-3", children: _jsx(AppLauncherHighlightCard, { highlight: highlight, density: "desktop", onSelect: selectHighlight }) })), orderedTiles.length === 0 ? (_jsx(AppLauncherEmptyState, { density: "desktop" })) : (_jsx(DesktopLauncherGrid, { sections: sections, currentPath: location.pathname, currentSearch: location.search, nav: nav, onSelect: select }))] })] }))] }));
};
export const TeeTimeMobileAppLauncherSection = ({ items, label, highlight, onNavigate }) => {
    const [query, setQuery] = React.useState('');
    const hasQuery = query.trim().length > 0;
    const groups = React.useMemo(() => groupAppLauncherItems(filterAppLauncherItems(items, query)), [items, query]);
    return (_jsxs("li", { className: "px-4 py-3", children: [_jsx("div", { className: "text-sm font-semibold text-gray-900", children: label }), _jsx("div", { className: "mt-3", children: _jsx(AppLauncherSearch, { id: "teetime-mobile-app-launcher-search", value: query, placeholder: "Search actions", onChange: setQuery }) }), _jsxs("div", { className: "mt-3 space-y-3", children: [!hasQuery && highlight && (_jsx(AppLauncherHighlightCard, { highlight: highlight, density: "mobile", onSelect: () => {
                            setQuery('');
                            onNavigate();
                        } })), groups.length === 0 ? (_jsx(AppLauncherEmptyState, { density: "mobile" })) : (_jsx(AppLauncherSections, { groups: groups, density: "mobile", onSelect: (item) => {
                            recordRecentItem(item.key);
                            setQuery('');
                            onNavigate();
                        } }))] })] }));
};
//# sourceMappingURL=TeeTimeAppLauncher.js.map